import React from 'react';
import { Checkbox } from '@ark-ui/react/checkbox';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface CheckboxInputProps extends BaseInputProps<boolean> {
  /** Volitelný popisek vedle checkboxu. */
  checkboxLabel?: string;
  className?: string;
}

/**
 * Atomický checkbox postavený na Ark UI `Checkbox`.
 *
 * Interně mapuje RHF `value` (boolean) na Ark `checked` a naopak. QA atributy
 * se propisují na skrytý nativní `<input>` (`Checkbox.HiddenInput`), takže
 * `data-*` stavy jsou dostupné ve formulářovém DOM stromu.
 *
 * Přístupné jméno je deterministické přes `aria-labelledby`: vždy popisek
 * z `<FormField />`, a pokud je zadán `checkboxLabel`, připojí se za něj —
 * jinak by si o jméno konkurovaly dva `<label>` elementy (FormField `htmlFor`
 * a Ark `Root`, který je sám `<label>`).
 */
export const CheckboxInput: React.FC<CheckboxInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  checkboxLabel,
  className,
}) => {
  const checked = !!value;
  const innerLabelId = `${id}-inner-label`;
  const checkboxIds = React.useMemo(
    () => ({ hiddenInput: id, label: innerLabelId }),
    [id, innerLabelId]
  );

  return (
    <Checkbox.Root
      checked={checked}
      disabled={disabled}
      readOnly={readOnly}
      invalid={hasError}
      onCheckedChange={(details) => onChange(details.checked === true)}
      onBlur={onBlur}
      className={clsx(styles.checkboxRoot, className)}
      data-selected={checked ? 'true' : 'false'}
      ids={checkboxIds}
    >
      <Checkbox.Control className={styles.checkboxControl}>
        <Checkbox.Indicator>
          <Icon name="check" size={14} />
        </Checkbox.Indicator>
      </Checkbox.Control>
      {checkboxLabel && (
        <Checkbox.Label className={styles.checkboxLabel}>
          {checkboxLabel}
        </Checkbox.Label>
      )}
      <Checkbox.HiddenInput
        ref={ref}
        name={name}
        aria-labelledby={
          checkboxLabel ? `${labelId} ${innerLabelId}` : labelId
        }
        aria-describedby={describedBy}
        {...testAttributes}
      />
    </Checkbox.Root>
  );
};

CheckboxInput.displayName = 'CheckboxInput';
